/**
 * Reports › Returns: of the sales made in a period, how many came back and
 * what was credited, by supplier (the quality measure), buyer, campaign,
 * source or reason. Worst return rate first.
 */
import { useState } from 'react';
import { Alert, Group, Loader, Stack, Table, Text, TextInput } from '@mantine/core';
import { formatRate, useReturnsReport, type ReturnsGroupBy } from '../api/returns';
import { formatGBP, formatNumber, TOKENS } from '../theme';
import { MonoSegmented } from '../ui';
import { useVocab } from '../vocab';

function ymd(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(d);
}

/** A thin bar for the rate, so the worst stand out down the column. */
function RateBar({ rate }: { rate: number | null | undefined }) {
  const pct = Math.min(100, Math.max(0, (rate ?? 0) * 100));
  return (
    <div style={{ width: 80, height: 6, background: TOKENS.surface2, borderRadius: 3, overflow: 'hidden' }} aria-hidden>
      <div style={{ width: `${pct}%`, height: '100%', background: pct >= 10 ? TOKENS.red : TOKENS.textSecondary }} />
    </div>
  );
}

export function ReportsReturns() {
  const v = useVocab();
  const [groupBy, setGroupBy] = useState<ReturnsGroupBy>('supplier');
  const [from, setFrom] = useState(() => ymd(new Date(Date.now() - 29 * 86400000)));
  const [to, setTo] = useState(() => ymd(new Date()));
  const { data, isPending, error, isPlaceholderData } = useReturnsReport(groupBy, from, to);
  const fig = { fontFamily: 'var(--mantine-font-family-monospace)', fontVariantNumeric: 'tabular-nums', fontSize: 12.5 } as const;
  const byReason = groupBy === 'reason';
  const heading: Record<ReturnsGroupBy, string> = {
    supplier: v.One('supplier'),
    buyer: v.One('buyer'),
    campaign: v.One('campaign'),
    source: 'Source',
    reason: 'Reason',
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <MonoSegmented
          value={groupBy}
          onChange={(g) => setGroupBy(g as ReturnsGroupBy)}
          data={[
            { value: 'supplier', label: v.Many('supplier') },
            { value: 'buyer', label: v.Many('buyer') },
            { value: 'campaign', label: v.Many('campaign') },
            { value: 'source', label: 'Sources' },
            { value: 'reason', label: 'Reasons' },
          ]}
          aria-label="Group by"
        />
        <Group gap="xs">
          <TextInput aria-label="Sales from" type="date" size="xs" value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
          <Text size="xs" c="dimmed">to</Text>
          <TextInput aria-label="Sales to" type="date" size="xs" value={to} onChange={(e) => setTo(e.currentTarget.value)} />
        </Group>
      </Group>
      <Text size="xs" c="dimmed">
        {`Sales made in these days (UK time), and how many of them were returned, whenever. ${byReason ? 'Each reason\'s rate is against all sales.' : ''}`}
      </Text>
      {error && <Alert color="red" variant="light">{error.message}</Alert>}
      {isPending && <Group justify="center" p="xl"><Loader /></Group>}
      {data && (
        <div className={isPlaceholderData ? 'hf-stale' : 'hf-fresh'}>
          <Table.ScrollContainer minWidth={680}>
            <Table striped verticalSpacing="xs" data-testid="returns-report">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{heading[groupBy]}</Table.Th>
                  {!byReason && <Table.Th style={{ textAlign: 'right' }}>Sales</Table.Th>}
                  <Table.Th style={{ textAlign: 'right' }}>Returned</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Return rate</Table.Th>
                  <Table.Th />
                  <Table.Th style={{ textAlign: 'right' }}>Credited</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Waiting</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.rows.length === 0 && (
                  <Table.Tr><Table.Td colSpan={7}><Text c="dimmed" ta="center" py="md">No sales in these days.</Text></Table.Td></Table.Tr>
                )}
                {data.rows.map((r) => (
                  <Table.Tr key={r.key || '(none)'}>
                    <Table.Td><Text size="sm" fw={500}>{r.label}</Text></Table.Td>
                    {!byReason && <Table.Td style={{ textAlign: 'right', ...fig }}>{formatNumber(r.sales)}</Table.Td>}
                    <Table.Td style={{ textAlign: 'right', ...fig }}>{formatNumber(r.returned)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right', ...fig }}>{formatRate(r.return_rate)}</Table.Td>
                    <Table.Td><RateBar rate={r.return_rate} /></Table.Td>
                    <Table.Td style={{ textAlign: 'right', ...fig }}>{formatGBP(r.credits)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right', ...fig }}>{r.requested ? formatNumber(r.requested) : ''}</Table.Td>
                  </Table.Tr>
                ))}
                <Table.Tr style={{ fontWeight: 600 }}>
                  <Table.Td><Text size="sm" fw={600}>All</Text></Table.Td>
                  {!byReason && <Table.Td style={{ textAlign: 'right', ...fig }}>{formatNumber(data.total.sales)}</Table.Td>}
                  <Table.Td style={{ textAlign: 'right', ...fig }}>{formatNumber(data.total.returned)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', ...fig }}>{formatRate(data.total.return_rate)}</Table.Td>
                  <Table.Td><RateBar rate={data.total.return_rate} /></Table.Td>
                  <Table.Td style={{ textAlign: 'right', ...fig }}>{formatGBP(data.total.credits)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', ...fig }}>{data.total.requested ? formatNumber(data.total.requested) : ''}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </div>
      )}
    </Stack>
  );
}
